/**
 * @file DesktopKeepConfirm.tsx
 * @description The confirm shown before a pin is created and before one is
 * removed. Consumer: `DesktopKeepControl`, which keeps exactly one of these
 * mounted and drives it through `open`.
 *
 * The shell stays mounted so antd owns the open/close transition, and
 * `destroyOnHidden` unmounts `DesktopKeepConfirmBody` — the only thing here that
 * reads the artifact's `attachments` map. `mode` is held by the caller across
 * the closing transition rather than derived from an `open` state, so the copy
 * does not change while the dialog animates out.
 *
 * Both directions confirm. The first pin is a download onto a person's disk and
 * the last unpin removes files from it; neither is a silent toggle.
 */

import { FC } from 'react';
import { Modal } from 'antd';

import DesktopKeepConfirmBody from './DesktopKeepConfirmBody';

export type DesktopKeepConfirmProps = {
    mode: 'keep' | 'stop';
    open: boolean;
    artifactLabel: 'form' | 'record';
    /** True while the pin write is in flight; holds the dialog open and the button spinning. */
    confirming: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

const DesktopKeepConfirm: FC<DesktopKeepConfirmProps> = ({
    mode,
    open,
    artifactLabel,
    confirming,
    onConfirm,
    onCancel,
}) => (
    <Modal
        open={open}
        destroyOnHidden
        title={mode === 'keep'
            ? `Keep this ${artifactLabel} on my computer`
            : `Stop keeping this ${artifactLabel}`}
        okText={mode === 'keep' ? 'Keep on my computer' : 'Stop keeping'}
        cancelText="Cancel"
        okButtonProps={{
            danger: mode === 'stop',
            'aria-label': mode === 'keep'
                ? `Confirm keeping this ${artifactLabel} on my computer`
                : `Confirm no longer keeping this ${artifactLabel}`,
        }}
        cancelButtonProps={{ 'aria-label': `Cancel desktop sync change for this ${artifactLabel}` }}
        confirmLoading={confirming}
        mask={{ closable: !confirming }}
        onOk={onConfirm}
        onCancel={onCancel}
    >
        <DesktopKeepConfirmBody mode={mode} artifactLabel={artifactLabel} />
    </Modal>
);

export default DesktopKeepConfirm;
